/**
 * Text Asset list permissions (VistoeraTASK-00689 / TASK-00690) — DISPLAY
 * side of the rules `text_asset_center_service` / `image_team_service.can_manage`
 * enforce. Hiding a menu item here is UX only; every API re-checks.
 *
 * Reuses Image Asset predicates — no parallel permission model.
 */
import {
  imageAssetPermissions,
  type ImageAssetPermissions,
  type PermissionItemLike,
  type PermissionTeamLike,
} from './imageAssetPermissions'

export interface TextPermissionItemLike extends PermissionItemLike {
  id?: string | null
  type?: string | null
  canonical_source_type?: string | null
}

export interface TextAssetPermissions extends ImageAssetPermissions {
  /** TYPE TEXT + WorkspaceTextAsset / CanvasNode:text_asset only. */
  canEdit: boolean
  /** True when team mode is on — personal WS hides share toggles. */
  showShareToggle: boolean
}

/** Only ordinary TEXT library notes are editable through this Center. */
export function isLibraryTextEditable(
  item: Pick<TextPermissionItemLike, 'type' | 'canonical_source_type'>,
): boolean {
  if (item.type !== 'TEXT') return false
  const src = item.canonical_source_type || ''
  return src === 'CanvasNode:text_asset' || src === 'WorkspaceTextAsset'
}

/**
 * VistoeraTASK-00690 — Delete is available for durable library rows
 * (any WorkspaceTextAsset type) and legacy CanvasNode:text_asset carriers.
 * AI Creative/Script/etc. that only exist as live Canvas / VideoDirector /
 * Transcript projections are NOT deletable here (own canonical domains).
 *
 * Important: Delete must NOT be gated the same way as Edit. Share/visibility
 * already uses can_manage for all library types; Delete must follow the same
 * manage gate or the ⋮ menu shows 设为仅自己 without 删除 (00689 regression).
 */
export function isLibraryTextDeletable(item: TextPermissionItemLike): boolean {
  const src = item.canonical_source_type || ''
  if (src === 'WorkspaceTextAsset') return true
  if (src === 'CanvasNode:text_asset') return true
  const id = item.id || ''
  if (id.startsWith('workspace_text:')) return true
  if (id.startsWith('canvas_node:') && item.type === 'TEXT') return true
  return false
}

export function textAssetPermissions(
  item: TextPermissionItemLike,
  team: PermissionTeamLike | null | undefined,
): TextAssetPermissions {
  const editable = isLibraryTextEditable(item)
  const deletable = isLibraryTextDeletable(item)
  // Personal Workspace (<2 members): team.enabled is false and team.user_id
  // may be omitted — treat deletable library notes as manageable, never show share.
  if (!team?.enabled) {
    return {
      isCreator: true,
      canOrganize: deletable,
      canDelete: deletable,
      canChangeVisibility: false,
      canEdit: editable,
      showShareToggle: false,
    }
  }
  const base = imageAssetPermissions(item, team)
  return {
    ...base,
    canEdit: editable && base.canDelete,
    // Manage gate matches visibility; type gate is deletable (library SoT).
    canDelete: deletable && base.canDelete,
    canChangeVisibility: base.canChangeVisibility,
    showShareToggle: true,
  }
}
